import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, MapPin } from "lucide-react";
import axios from "axios";
import CityAutocomplete from "./CityAutocomplete";
import CosmicLoader from "./CosmicLoader";
import { DobField, TobField } from "./DobTimeFields";
import { useI18n } from "../contexts/I18nContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * BirthForm — single canonical Kundli generation form.
 * On success it stores the report in localStorage and navigates to /kundli/report.
 */
export default function BirthForm({ compact = false }) {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", dob: "", tob: "12:00", city: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!form.dob) { setError(lang === "hi" ? "कृपया जन्म तिथि चुनें।" : "Please pick your date of birth."); return; }
    const [year, month, day] = form.dob.split("-").map(Number);
    const [hour, minute] = (form.tob || "12:00").split(":").map(Number);
    setError(""); setLoading(true);
    const body = { name: form.name, year, month, day, hour, minute, city: form.city, lang };
    // Quietly retry a cold or flaky engine instead of showing an error.
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const res = await axios.post(`${API}/kundli/generate`, body, { withCredentials: true });
        const payload = {
          ...res.data,
          birth_details: { name: form.name, year, month, day, hour, minute, city: form.city },
        };
        localStorage.setItem("astrofate_report", JSON.stringify(payload));
        navigate("/kundli/report");
        setLoading(false);
        return;
      } catch (err) {
        const status = err.response?.status;
        const worthRetry = !status || status === 502 || status === 503 || status === 504;
        if (worthRetry && attempt < 3) {
          await new Promise(r => setTimeout(r, attempt * 3000));
          continue;
        }
        setError(err.response?.data?.detail || "Failed to generate. Please try again.");
        break;
      }
    }
    setLoading(false);
  };

  return (
    <>
      {loading && (
        <CosmicLoader
          mode="overlay"
          title="Calculating Your Cosmic Blueprint"
          messages={[
            "Evaluating your birth details…",
            "Aligning planetary positions…",
            "Decoding your nakshatras…",
            "Drawing your divisional charts…",
            "Hold tight — the stars are speaking…",
            "Almost there — finalising predictions…",
          ]}
        />
      )}
    <div className="glass-card birth-form-card" style={{ padding: compact ? "22px" : "28px", border: "1px solid rgba(255,209,102,0.15)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "20px" }}>
        <Sparkles size={20} style={{ color: "#FFD166" }} />
        <h2 className="font-cinzel" style={{ fontSize: "1.15rem", color: "#FFD166" }}>{t("birth.title")}</h2>
      </div>
      <form onSubmit={handleGenerate} className="birth-friendly">
        <div style={{ marginBottom: "14px" }}>
          <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "block", marginBottom: "7px" }}>{t("birth.name")}</label>
          <input data-testid="birth-name-input" type="text" value={form.name} onChange={set("name")} autoComplete="given-name" placeholder={t("birth.namePh")} className="cosmic-input" style={{ fontSize: "1rem", padding: "13px 14px" }} required />
        </div>
        <DobField prefix="birth" label={t("chart.dob")} hi={lang === "hi"}
          value={form.dob} onChange={(v) => setForm(f => ({ ...f, dob: v }))} />
        <TobField prefix="birth" label={t("chart.tob")} hi={lang === "hi"}
          value={form.tob} onChange={(v) => setForm(f => ({ ...f, tob: v }))} />
        <div style={{ marginBottom: "18px" }}>
          <label style={{ color: "rgba(255,255,255,0.7)", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px", marginBottom: "7px" }}>
            <MapPin size={13} /> {t("birth.city")}
          </label>
          <CityAutocomplete value={form.city} onChange={(v) => setForm(f => ({ ...f, city: v }))} required name="city" />
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem", marginTop: "5px" }}>
            {lang === "hi" ? "शहर टाइप करें और सूची से चुनें।" : "Start typing your birth city and pick it from the list."}
          </p>
        </div>
        {error && <div style={{ color: "#fca5a5", fontSize: "0.82rem", marginBottom: "12px", padding: "8px 12px", background: "rgba(239,68,68,0.1)", borderRadius: "8px" }}>{error}</div>}
        <button data-testid="birth-submit-btn" type="submit" disabled={loading} className="btn-gold" style={{ width: "100%", fontSize: "1rem" }}>
          {loading ? (
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <span className="cosmic-spinner" style={{ width: "18px", height: "18px", border: "2px solid rgba(18,0,33,0.3)", borderTop: "2px solid #120021" }} />
              {t("birth.generating")}
            </span>
          ) : `✦ ${t("birth.generate")} ✦`}
        </button>
      </form>
    </div>
    </>
  );
}
